import { cacheGet, cacheSet } from "../lib/cache";

/**
 * Read cache synchronously. Returns null on miss/expired/null key.
 */
export function readCached(key) {
  return cacheGet(key);
}

/**
 * Stale-while-revalidate fetch: return cached data immediately when present,
 * optionally refresh in the background. On miss, awaits the network.
 *
 * @param {object} opts
 * @param {string|null} opts.key - user-scoped cache key; null skips cache
 * @param {number} opts.ttl - TTL in ms
 * @param {() => Promise<any>} opts.fetcher
 * @param {boolean} [opts.revalidate=false] - force network, skip cache read
 * @returns {Promise<{ data: any, fromCache: boolean }>}
 */
export async function fetchCached({ key, ttl, fetcher, revalidate = false }) {
  if (!key || revalidate) {
    const data = await fetcher();
    if (key) cacheSet(key, data, ttl);
    return { data, fromCache: false };
  }

  const cached = cacheGet(key);
  if (cached !== null) {
    fetcher()
      .then((data) => cacheSet(key, data, ttl))
      .catch(() => {});
    return { data: cached, fromCache: true };
  }

  const data = await fetcher();
  cacheSet(key, data, ttl);
  return { data, fromCache: false };
}
